import React, { FC } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AddCircle, Delete } from '@material-ui/icons';
import { IAgeRule } from '../age-rule';
import { AnimalType } from '../../../../../models/animal';
import {
  DiseaseViewModel,
  FarmMedicalSupplyViewModel,
  NotificationRuleRegionType
} from '../../../../../models/injection';
import { AnimalTypeSelect, FarmDiseaseSelect, FarmMedicalSupplySelect } from '../../../../../elements';

export interface IPercentageTabProps {
  isDisabled: boolean;
  animalType: AnimalType;
  setAnimalType: React.Dispatch<React.SetStateAction<AnimalType>>;
  ageRules: IAgeRule[];
  setAgeRules: React.Dispatch<React.SetStateAction<IAgeRule[]>>;
  farmSupplyId: string;
  farmDiseaseId: string;
  setFarmSupplyId: React.Dispatch<React.SetStateAction<string>>;
  setFarmDiseaseId: React.Dispatch<React.SetStateAction<string>>;
  farmSupplies: FarmMedicalSupplyViewModel[];
  farmDiseases: DiseaseViewModel[];
  loadingMedSupp: boolean;
  loadingDiseases: boolean;
  isLactating?: boolean;
  isPregnant?: boolean;
  setIsLactating: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setIsPregnant: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  notificationRuleRegionType: NotificationRuleRegionType;
  allowMultipleRules: boolean;
}

const PercentageRuleTab: FC<IPercentageTabProps> = (props) => {
  const { t } = useTranslation();

  const {
    animalType,
    ageRules,
    setAgeRules,
    setAnimalType,
    farmSupplyId,
    farmDiseaseId,
    setFarmSupplyId,
    setFarmDiseaseId,
    farmSupplies,
    farmDiseases,
    loadingMedSupp,
    loadingDiseases,
    isLactating,
    isPregnant,
    setIsPregnant,
    setIsLactating,
    notificationRuleRegionType,
    allowMultipleRules
  } = props;

  const handleAgeFromChange = (index: number, value: string) => {
    ageRules[index].from = value;
    setAgeRules([...ageRules]);
  };

  const handleAgeTillChange = (index: number, value: string) => {
    ageRules[index].till = value;
    setAgeRules([...ageRules]);
  };

  const handlePercentageChange = (index: number, value: string) => {
    ageRules[index].percentage = value;
    setAgeRules([...ageRules]);
  };

  const handleDeleteRule = (index: number) => {
    ageRules.splice(index, 1);
    setAgeRules([...ageRules]);
  };

  const addNewAgeRule = () => {
    if (!allowMultipleRules) return;

    const prevTill = Number(ageRules[ageRules.length - 1].till);
    setAgeRules([
      ...ageRules,
      {
        till: '',
        percentage: '',
        from: prevTill > 0 ? (prevTill + 1).toString() : ''
      }
    ]);
  };

  return (
    <Box p={1} component="form">
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={6}>
          <AnimalTypeSelect fullWidth value={animalType} onAnimalTypeChange={(a) => setAnimalType(a)} allowAny />
        </Grid>

        {notificationRuleRegionType === NotificationRuleRegionType.Injection && (
          <Grid item xs={12} sm={5}>
            <FarmMedicalSupplySelect
              fullWidth
              disabled={loadingMedSupp}
              supplies={farmSupplies}
              value={farmSupplyId}
              onSupplyChange={setFarmSupplyId}
            />
          </Grid>
        )}

        {notificationRuleRegionType === NotificationRuleRegionType.Mortality && (
          <Grid item xs={12} sm={5}>
            <FarmDiseaseSelect
              fullWidth
              disabled={loadingDiseases}
              diseases={farmDiseases}
              value={farmDiseaseId}
              onSupplyChange={setFarmDiseaseId}
            />
          </Grid>
        )}

        {allowMultipleRules && (
          <Grid item xs={2} sm={1}>
            <IconButton onClick={() => addNewAgeRule()}>
              <AddCircle color="primary" />
            </IconButton>
          </Grid>
        )}

        {(animalType === AnimalType.Sow || animalType === AnimalType.Gilt) && (
          <Grid item container spacing={1} alignItems="center">
            <Grid item xs={6}>
              <FormControlLabel
                label={t('f-animal-preg-only')}
                control={
                  <Checkbox
                    color="primary"
                    checked={isPregnant || false}
                    onChange={(e, _v) => setIsPregnant(e.target.checked)}
                    name="checkedF"
                  />
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                label={t('f-animal-is-lact')}
                control={
                  <Checkbox
                    color="primary"
                    checked={isLactating || false}
                    onChange={(e, _v) => setIsLactating(e.target.checked)}
                  />
                }
              />
            </Grid>
          </Grid>
        )}

        {ageRules.map((_v, i) => (
          <Grid item container spacing={1} alignItems="center" key={i}>
            <Grid item xs={6} sm={4}>
              <TextField
                label={t('f-notif-rule-from')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('f-day-s')}</InputAdornment>
                }}
                value={ageRules[i].from}
                onChange={(e) => handleAgeFromChange(i, e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label={t('f-notif-rule-till')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('f-day-s')}</InputAdornment>
                }}
                value={ageRules[i].till}
                onChange={(e) => handleAgeTillChange(i, e.target.value)}
              />
            </Grid>
            <Grid item xs={10} sm={3}>
              <TextField
                label={t('f-supp-rule-perc')}
                variant="outlined"
                fullWidth
                size="small"
                type="number"
                value={ageRules[i].percentage}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                onChange={(e) => handlePercentageChange(i, e.target.value)}
              />
            </Grid>
            <Grid item xs={2} sm={1}>
              <IconButton disabled={i === 0} onClick={(_) => handleDeleteRule(i)}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PercentageRuleTab;
